import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Button, Chip,
    TextField, MenuItem, Tabs, Tab, InputAdornment, Dialog,
    DialogTitle, DialogContent, DialogActions, IconButton
} from '@mui/material';
import {
    Search as SearchIcon, Download as DownloadIcon, Add as AddIcon,
    Visibility as ViewIcon, Edit as EditIcon, Delete as DeleteIcon,
    Notifications as NoticeIcon, Print as PrintIcon, Send as SendIcon
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-toastify';

const Notices = () => {
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [viewDialog, setViewDialog] = useState(false);
    const [selectedNotice, setSelectedNotice] = useState(null);
    const [formData, setFormData] = useState({ title: '', content: '', category: '', audience: '', priority: '' });

    useEffect(() => { fetchNotices(); }, []);

    const fetchNotices = async () => {
        try {
            const res = await api.get('/notices');
            setNotices(res.data.data || []);
        } catch (error) {
            setNotices([
                { id: 1, title: 'Annual Day Celebration', content: 'Annual Day will be held on March 15, 2026. All parents are cordially invited. Students to attend rehearsals from March 10.', category: 'Event', audience: 'All', priority: 'High', createdBy: 'Principal', date: '2026-02-10', status: 'Published' },
                { id: 2, title: 'Fee Payment Reminder', content: 'Last date for fee payment for Term 2 is February 28, 2026. Late fees will be applicable after the due date.', category: 'Fee', audience: 'Parents', priority: 'High', createdBy: 'Admin', date: '2026-02-08', status: 'Published' },
                { id: 3, title: 'Holiday Notice - Shivaji Jayanti', content: 'School will remain closed on February 19, 2026 on account of Shivaji Jayanti.', category: 'Holiday', audience: 'All', priority: 'Medium', createdBy: 'Principal', date: '2026-02-12', status: 'Published' },
                { id: 4, title: 'Unit Test 2 Schedule', content: 'Unit Test 2 will begin from March 5, 2026. Detailed timetable is available at the office.', category: 'Exam', audience: 'Students', priority: 'High', createdBy: 'Exam Coordinator', date: '2026-02-14', status: 'Published' },
                { id: 5, title: 'Staff Meeting Notice', content: 'Staff meeting scheduled for February 20 at 3:00 PM in the conference hall.', category: 'Meeting', audience: 'Staff', priority: 'Medium', createdBy: 'Principal', date: '2026-02-15', status: 'Draft' },
                { id: 6, title: 'Sports Day Preparation', content: 'Sports Day tryouts begin next week. Students interested in participating should register with their PT teacher.', category: 'Event', audience: 'Students', priority: 'Low', createdBy: 'Sports Teacher', date: '2026-02-13', status: 'Draft' },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const filteredNotices = notices.filter(n => {
        const matchTab = tab === 0 ? true : tab === 1 ? n.status === 'Published' : n.status === 'Draft';
        const matchSearch = !searchTerm || n.title.toLowerCase().includes(searchTerm.toLowerCase()) || n.category.toLowerCase().includes(searchTerm.toLowerCase());
        return matchTab && matchSearch;
    });

    const publishedCount = notices.filter(n => n.status === 'Published').length;
    const draftCount = notices.filter(n => n.status === 'Draft').length;

    const getPriorityColor = (p) => p === 'High' ? 'error' : p === 'Medium' ? 'warning' : 'info';

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NoticeIcon sx={{ color: '#1a9e8f', fontSize: 28 }} />
                    <Typography variant="h5" fontWeight={700}>Notice Board</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={() => setOpenDialog(true)}>Create Notice</Button>
                    <Button variant="outlined" startIcon={<PrintIcon />} size="small">Print</Button>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Total Notices</Typography>
                        <Typography variant="h4" fontWeight={700}>{notices.length}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={4}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Published</Typography>
                        <Typography variant="h4" fontWeight={700} color="success.main">{publishedCount}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={4}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Drafts</Typography>
                        <Typography variant="h4" fontWeight={700} color="warning.main">{draftCount}</Typography>
                    </CardContent></Card>
                </Grid>
            </Grid>

            <Card>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tab label={`All (${notices.length})`} />
                    <Tab label={`Published (${publishedCount})`} />
                    <Tab label={`Drafts (${draftCount})`} />
                </Tabs>
                <Box sx={{ p: 2 }}>
                    <TextField fullWidth size="small" placeholder="Search notices..." value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)} sx={{ mb: 2 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Audience</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredNotices.map(n => (
                                    <TableRow key={n.id} hover>
                                        <TableCell sx={{ fontWeight: 600, maxWidth: 250 }}>{n.title}</TableCell>
                                        <TableCell><Chip label={n.category} size="small" variant="outlined" /></TableCell>
                                        <TableCell>{n.audience}</TableCell>
                                        <TableCell><Chip label={n.priority} size="small" color={getPriorityColor(n.priority)} /></TableCell>
                                        <TableCell>{n.createdBy}</TableCell>
                                        <TableCell>{new Date(n.date).toLocaleDateString()}</TableCell>
                                        <TableCell><Chip size="small" label={n.status} color={n.status === 'Published' ? 'success' : 'default'} /></TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <IconButton size="small" onClick={() => { setSelectedNotice(n); setViewDialog(true); }}><ViewIcon fontSize="small" /></IconButton>
                                                <IconButton size="small"><EditIcon fontSize="small" /></IconButton>
                                                <IconButton size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Card>

            {/* View Notice */}
            <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedNotice?.title}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Chip label={selectedNotice?.category} size="small" variant="outlined" />
                        <Chip label={selectedNotice?.priority} size="small" color={getPriorityColor(selectedNotice?.priority)} />
                        <Chip label={selectedNotice?.audience} size="small" />
                    </Box>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{selectedNotice?.content}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        Published by {selectedNotice?.createdBy} on {selectedNotice?.date}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialog(false)}>Close</Button>
                    <Button variant="outlined" startIcon={<PrintIcon />}>Print</Button>
                    <Button variant="contained" startIcon={<SendIcon />}>Notify All</Button>
                </DialogActions>
            </Dialog>

            {/* Create Notice */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create Notice</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Title" fullWidth value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
                        <TextField label="Content" fullWidth multiline rows={4} value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} />
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <TextField select label="Category" fullWidth value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                                    {['Event', 'Fee', 'Holiday', 'Exam', 'Meeting', 'General'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={4}>
                                <TextField select label="Audience" fullWidth value={formData.audience} onChange={(e) => setFormData({ ...formData, audience: e.target.value })}>
                                    {['All', 'Students', 'Parents', 'Staff', 'Teachers'].map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={4}>
                                <TextField select label="Priority" fullWidth value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })}>
                                    {['High', 'Medium', 'Low'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                </TextField>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="outlined" onClick={() => { toast.info('Saved as draft'); setOpenDialog(false); }}>Save Draft</Button>
                    <Button variant="contained" onClick={() => { toast.success('Notice published'); setOpenDialog(false); }}>Publish</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Notices;
